import {
  Component,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useJoyride, type Step } from 'react-joyride';
import { OnboardingTourArrow, OnboardingTourTooltip } from './onboarding-tour-tooltip';
import { selectTour, type SelectedTour } from './onboarding-tours';

/** Mesmo ponto de corte do `md:` do Tailwind, usado pelo shell. */
const MOBILE_QUERY = '(max-width: 767px)';

/** As telas carregam dados antes dos alvos existirem, então tentamos algumas vezes. */
const SELECT_INTERVAL_MS = 700;
const SELECT_ATTEMPTS = 5;

/** Rota de partida quando o usuário pede o tutorial fora de uma tela com tour. */
const REPLAY_FALLBACK_ROUTE = '/dashboard';

const PLACEHOLDER_STEP: Step = {
  target: 'body',
  content: '',
  hideOverlay: true,
};

interface OnboardingTourContextValue {
  /** Inicia o tutorial da rota atual (ou redireciona para o dashboard). */
  replayTour: () => void;
  isRunning: boolean;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

export function useOnboardingTour(): OnboardingTourContextValue | null {
  return useContext(OnboardingTourContext);
}

const targetExists = (target: string): boolean => !!document.querySelector(target);

/** Se o Joyride quebrar, o app continua; só o overlay some. */
class TourErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}

export function OnboardingTourProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(MOBILE_QUERY).matches,
  );
  const [active, setActive] = useState<SelectedTour | null>(null);
  const [startRequestId, setStartRequestId] = useState(0);
  const pendingStartRef = useRef(false);
  const activeRef = useRef<SelectedTour | null>(null);
  activeRef.current = active;

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const steps = useMemo<Step[]>(
    () =>
      (active?.steps ?? []).map(({ target, title, content, placement }) => ({
        target,
        title,
        content,
        ...(placement ? { placement } : {}),
      })),
    [active],
  );

  // Joyride v3 quebra se `steps` virar [] no meio do ciclo de encerramento.
  const joyrideSteps = steps.length > 0 ? steps : [PLACEHOLDER_STEP];

  const { controls, state, on, Tour } = useJoyride({
    continuous: true,
    steps: joyrideSteps,
    tooltipComponent: OnboardingTourTooltip,
    arrowComponent: OnboardingTourArrow,
    options: {
      skipBeacon: true,
      spotlightPadding: 8,
      spotlightRadius: 16,
      overlayClickAction: false,
      closeButtonAction: 'skip',
      blockTargetInteraction: true,
      targetWaitTimeout: 3000,
      scrollOffset: 96,
      zIndex: 1000,
      width: 'auto',
      // O chrome visual fica na Card do HeroUI; aqui só some o fundo branco do Joyride.
      backgroundColor: 'transparent',
      arrowColor: 'var(--lp-surface)',
    },
    locale: {
      back: 'Voltar',
      close: 'Fechar',
      last: 'Entendi',
      next: 'Próximo',
      skip: 'Pular',
    },
    styles: {
      tooltip: { backgroundColor: 'transparent', padding: 0, borderRadius: 0 },
    },
  });

  useEffect(() => {
    return on('tour:end', () => {
      if (!activeRef.current) return;
      setActive(null);
    });
  }, [on]);

  useEffect(() => {
    if (active && steps.length > 0) controls.start(0);
  }, [active, steps.length, controls]);

  /** Só roda quando o menu pediu o tour e redirecionou para uma rota com alvos. */
  useEffect(() => {
    if (!pendingStartRef.current || active) return;

    let attempts = 0;
    const attemptStart = (): boolean => {
      attempts += 1;
      const selected = selectTour({
        pathname,
        isMobile,
        exists: targetExists,
      });
      if (selected) {
        pendingStartRef.current = false;
        setActive(selected);
        return true;
      }
      return attempts >= SELECT_ATTEMPTS;
    };

    if (attemptStart()) {
      if (attempts >= SELECT_ATTEMPTS) pendingStartRef.current = false;
      return;
    }

    const timer = window.setInterval(() => {
      if (attemptStart()) {
        window.clearInterval(timer);
        if (attempts >= SELECT_ATTEMPTS) pendingStartRef.current = false;
      }
    }, SELECT_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [pathname, isMobile, active, startRequestId]);

  const replayTour = useCallback(() => {
    const selected = selectTour({
      pathname,
      isMobile,
      exists: targetExists,
    });

    if (selected) {
      setActive(selected);
      return;
    }

    pendingStartRef.current = true;
    setStartRequestId((id) => id + 1);
    if (!pathname.startsWith(REPLAY_FALLBACK_ROUTE)) {
      navigate(REPLAY_FALLBACK_ROUTE);
    }
  }, [pathname, isMobile, navigate]);

  const value = useMemo<OnboardingTourContextValue>(
    () => ({ replayTour, isRunning: state.status === 'running' }),
    [replayTour, state.status],
  );

  return (
    <OnboardingTourContext.Provider value={value}>
      {children}
      <TourErrorBoundary>{Tour}</TourErrorBoundary>
    </OnboardingTourContext.Provider>
  );
}
